import { getGSAPSettings } from '../utils/GSAP';
import { logWarn, showErrorOnElement } from './errors.js';

const checkRevealHiddenText = document.querySelectorAll('[vdx-gsap-revealtext]');

if (checkRevealHiddenText.length > 0){  
    const firstEl = checkRevealHiddenText[0];
    if (typeof gsap === 'undefined') {  
        logWarn('GSAP is missing. Please turn on GSAP in Webflow.', { module: 'GSAPRevealHiddenText' });
        showErrorOnElement(firstEl, 'GSAP is missing. Please turn on GSAP in Webflow.');
    }
    if (typeof ScrollTrigger === 'undefined') {
        logWarn('ScrollTrigger not found. Activate it in Webflow for animations.', { module: 'GSAPRevealHiddenText' });
        showErrorOnElement(firstEl, 'ScrollTrigger not found. Activate it in Webflow for animations.');
    }
    if (typeof SplitText === 'undefined') {
        logWarn('GSAP SplitText plugin is required. Enable it in Webflow settings.', { module: 'GSAPRevealHiddenText' });
        showErrorOnElement(firstEl, 'GSAP SplitText plugin is required. Enable it in Webflow settings.');
    }
}

if (checkRevealHiddenText.length > 0 && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined' && typeof SplitText !== 'undefined'){
    gsap.registerPlugin(ScrollTrigger, SplitText);
}   

export const revealHiddenText = () => {
    if (checkRevealHiddenText.length > 0 && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined' && typeof SplitText !== 'undefined'){
        const revealHiddenTextElements = gsap.utils.toArray('[vdx-gsap-revealtext]');
    
        if (revealHiddenTextElements.length === 0) {
            return;
        }

        // Add CSS styles once for all elements
        if (!document.getElementById('vdx-gsap-revealtext-styles')) {
            const style = document.createElement('style');
            style.id = 'vdx-gsap-revealtext-styles';
            style.textContent = `
            .split-line, .split-word, .split-char {
                display: inline-block;
                position: relative;
            }
            .split-word {
                margin-right: 0.0em;
            }
            .split-char {
                margin-right: 0;
            }
            .reveal-text-wrapper {
                position: relative;
                overflow: hidden;
            }
            .reveal-element-wrapper {
                position: relative;
                overflow: hidden;
                display: inline-block;
            }
            .reveal-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 1;
            }
        `;
            document.head.appendChild(style);
        }

    // Create a single IntersectionObserver for all elements
    const observer = new IntersectionObserver((entries) => {
        
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const el = entry.target;
                const preparedData = el._preparedRevealData;
                
                if (preparedData) {
                    triggerAnimation(preparedData);
                }
                
                observer.unobserve(el);
            }
        });
    }, {
        threshold: 0.2
    });

    // Process all elements and observe them
    revealHiddenTextElements.forEach(el => {
        const preparedData = prepareElement(el);
        observer.observe(el);
    });

    function prepareElement(el) {
        const direction = el.getAttribute('vdx-gsap-revealtext') || 'line-right';
        
        // Check if animation should be disabled (value is 'false')
        if (direction === 'false') {
            // Make element visible without animation
            gsap.set(el, { opacity: 1, clearProps: "x,y,scale,rotation,rotationX" });
            return;
        }
        
        const { duration, stagger, ease, disableBreakpoint } = getGSAPSettings(el);
        const backgroundColor = getComputedStyle(document.body).backgroundColor || '#ffffff';

        // Check if animations should be disabled based on breakpoint
        if (disableBreakpoint > 0 && window.innerWidth <= disableBreakpoint) {
            // Make all children visible without animation
            gsap.set(el.children, { opacity: 1, clearProps: "x,y,scale,rotation,rotationX" });
            return;
        }
        
        // Create wrapper with class instead of inline styles
        const wrapper = document.createElement('div');
        wrapper.className = 'reveal-text-wrapper';
        
        // Clone and prepare content
        const content = el.cloneNode(true);
        content.removeAttribute('vdx-gsap-revealtext');
        content.removeAttribute('vdx-gsap-duration');
        content.removeAttribute('vdx-gsap-stagger');
        content.removeAttribute('vdx-gsap-ease');
        content.style.visibility = 'visible';
        content.style.opacity = '1';
        
        // Set up structure
        el.innerHTML = '';
        wrapper.appendChild(content);
        el.appendChild(wrapper);
        
        gsap.set(content, { position: 'relative', zIndex: 0 });
        
        // Determine split type based on direction prefix
        const splitType = direction.startsWith('words-') ? "words" : 
                          direction.startsWith('chars-') ? "chars" : 
                          "lines";
        
        // Split the text
        const split = new SplitText(content, { 
            type: splitType,
            linesClass: "split-line",
            wordsClass: "split-word",
            charsClass: "split-char"
        });
        
        // Get elements to animate
        const elements = split[splitType];
        
        // Create overlays efficiently
        elements.forEach(element => {
            const elementWrapper = document.createElement('div');
            elementWrapper.className = 'reveal-element-wrapper';
            
            const overlay = document.createElement('div');
            overlay.className = 'reveal-overlay';
            overlay.style.backgroundColor = backgroundColor;
            
            const parent = element.parentNode;
            parent.insertBefore(elementWrapper, element);
            elementWrapper.appendChild(element);
            elementWrapper.appendChild(overlay);
            
            element.overlay = overlay;
        });
        
        // Determine animation properties
        const directionSuffix = direction.split('-')[1] || 'right';
        let animProps;
        
        switch(directionSuffix) {
            case 'right': animProps = { xPercent: 100 }; break;
            case 'up': animProps = { yPercent: -100 }; break;
            case 'down': animProps = { yPercent: 100 }; break;
            case 'left': 
            default: animProps = { xPercent: -100 };
        }
        
        // Store data for animation
        const preparedData = {
            elements: elements,
            animProps: animProps,
            duration: duration,
            ease: ease,
            stagger: stagger
        };
        
        el._preparedRevealData = preparedData;
        return preparedData;
    }

    function triggerAnimation(preparedData) {
        const { elements, animProps, duration, ease, stagger } = preparedData;
        
        gsap.to(elements.map(element => element.overlay), {
            ...animProps,
            duration: duration,
            ease: ease,
            stagger: stagger
        });
    }
    }
}